import type { Cell, Column, Header } from "@tanstack/react-table";
import type { ReactElement } from "react";
import { twMerge } from "tailwind-merge";

import type {
  CellMetaWithSimpleOnChange,
  LocationCellContext,
  LocationDraftField,
  LocationDraftKey,
  RowMeta,
} from "@/types/manage-locations";
import {
  IndividualScreenerLocationStatus,
  type ScreenerLocation,
  type ScreenerLocationDraft,
} from "@/types/screener-location";
import type { Nullable } from "@/types/utils";
import { numberIsTruthyOrZero } from "@/utils/helpers";
import { isEmptyAfterTrim } from "@/utils/string";
import { draftWagesAreValid, getDraftWageInfoFromLocation } from "@/utils/wage";

// Just to avoid magic strings throughout.
export const MANAGE_LOCATIONS_COLUMN_FIELDS = {
  actions: "actions",
  atsJobId: "atsJobId",
  locationName: "locationName",
  maxPayHourly: "maxPayHourly",
  minPayHourly: "minPayHourly",
  sessionCount: "sessionCount",
  shareLinkSlug: "shareLinkSlug",
  status: "status",
  wageAmount: "wageAmount",
  wageType: "wageType",
} as const;

export const getTableMetaAndLocationId = <TValue,>({
  row: { index, original: location },
  table: {
    options: { meta },
  },
}: LocationCellContext<TValue>): RowMeta => {
  const manageLocationsMeta = meta?.manageLocations;

  if (!manageLocationsMeta) {
    throw new TypeError(
      "Attempted to resolve Manage Locations Table meta but it was not defined.",
    );
  }

  const isEditing = location.id === manageLocationsMeta.editingLocationId;
  return {
    ...manageLocationsMeta,
    // New locations are always a draft until being saved
    draft: location.isNew ? location : manageLocationsMeta.draft,
    index,
    isEditing,
    locationId: location.id,
  };
};

type BuildCell<
  Field extends LocationDraftKey,
  TValue,
  Meta extends RowMeta | CellMetaWithSimpleOnChange<Field>,
> = (info: LocationCellContext<TValue>, meta: Meta) => ReactElement;

// Utility function for including all table metadata as well as the current
// row's ID.
export const withMetaAndLocationId =
  <TValue,>(buildCell: BuildCell<LocationDraftKey, TValue, RowMeta>) =>
  (info: LocationCellContext<TValue>) =>
    buildCell(info, getTableMetaAndLocationId(info));

// Wrapper for `withMetaAndLocationId` that replaces the change function with
// a simple (value: TValue) => void for ease of use within components that don't
// need access to multiple properties.
export const withSimpleOnCellChange = <Field extends LocationDraftKey>(
  field: Field,
  buildCell: BuildCell<
    Field,
    LocationDraftField<Field>,
    CellMetaWithSimpleOnChange<Field>
  >,
) =>
  withMetaAndLocationId(
    (info: LocationCellContext<LocationDraftField<Field>>, meta: RowMeta) => {
      const { original: location } = info.row;

      if (location.isNew) {
        const onChange = (value: LocationDraftField<Field>) =>
          meta.onCellChange({ [field]: value }, location.id);

        return buildCell(info, {
          ...meta,
          // Fall back to row value for new locations rather than an existing
          // location's draft
          draftValue: location[field],
          index: info.row.index,
          isEditing: true,
          onChange,
        });
      }

      const onChange = (value: LocationDraftField<Field>) => {
        meta.onCellChange({ [field]: value });
      };
      return buildCell(info, {
        ...meta,
        draftValue: meta.draft?.[field],
        index: info.row.index,
        onChange,
      });
    },
  );

export const convertLocationToDraft = (
  location: ScreenerLocation,
): ScreenerLocationDraft => {
  const { atsJobId, id, locationName, sessionCount, shareLinkSlug, status } =
    location;

  return {
    atsJobId,
    id,
    isNew: false,
    locationName,
    sessionCount,
    shareLinkSlug,
    status,
    ...getDraftWageInfoFromLocation(location),
  };
};

export const getRemoveLocationAllowed = (
  { isNew, sessionCount, status }: ScreenerLocationDraft,
  isPublishedScreener: boolean,
  rowCount: number,
  publishedLocationsCount: number,
) => {
  // Each screener must have at least one location
  if (rowCount === 1) {
    return false;
  }

  // Drafts can always be removed, so long as they are not the only
  // location in the table (as might be the case during initial setup)
  if (isNew) {
    return true;
  }

  if (isPublishedScreener) {
    return (
      // publishedLocationsCount differs from rowCount by only
      // including non-draft locations. This is to prevent removal when
      // any number of drafts exist, but only one actual location
      // remains
      publishedLocationsCount >= 1 &&
      // Prevent an edge case where sessions could be added as (or just
      // before) a location is being deleted
      status === IndividualScreenerLocationStatus.Paused &&
      // Never delete locations which include sessions, this is for
      // liability reasons and to prevent loss of data
      (sessionCount ?? 0) === 0
    );
  }

  // Unpublished screeners do not display a status column and, being
  // unpublished, cannot have sessions.
  return true;
};

const shareLinkSlugIsValid = (
  slug?: Nullable<string>,
): [valid: false, message: string] | [valid: true, message: undefined] => {
  if (!slug) {
    return [true, undefined];
  }

  if (slug.length > 0 && slug.length < 4) {
    return [false, "Short link slug must be at least 4 characters"];
  }

  if (!new RegExp(/^[a-zA-Z0-9_-]*$/).test(slug)) {
    return [false, "Short slug must use URL safe characters only"];
  }

  return [true, undefined];
};

export const locationDraftValidator = (
  draft: Nullable<ScreenerLocationDraft>,
) => ({
  locationName: !isEmptyAfterTrim(draft?.locationName),
  shareLinkSlug: shareLinkSlugIsValid(draft?.shareLinkSlug),
  wages: draftWagesAreValid(draft),
});

export const addCommonAnimationClassNames = (
  currentClassNames: string[] = [],
) =>
  currentClassNames.concat([
    "ease-out",
    "duration-300",
    "motion-reduce:transition-none",
  ]);

export const getInputCellAnimationClassNames = (isEditing: boolean) => {
  const staticClassNames = addCommonAnimationClassNames([
    "disabled:bg-transparent",
    "disabled:cursor-default",
    "focus:border-uw-purple",
    "outline-none",
    "transition-[border-color,field-sizing,padding-left,width]",
  ]);

  return twMerge(
    staticClassNames,
    isEditing
      ? ["border-uw-gray-2", "pl-3.5"]
      : [
          "border-uw-gray-2/0",
          "pl-0",
          "placeholder:text-uw-gray-9",
          "disabled:cursor-text",
          "disabled:text-uw-gray-9",
          "disabled:opacity-100",
        ],
  );
};

export const getSharedManageLocationsCellClassNames = (
  cell:
    | Cell<ScreenerLocationDraft, unknown>
    | Header<ScreenerLocationDraft, unknown>,
  isLastRow = false,
  cellIndex?: number,
  cellsInRow?: number,
) => {
  const isHeaderCell = "headerGroup" in cell;
  // The cell preceding the actions cell needs additional padding to comfortably
  // fit beside the sticky actions cell. Which cell precedes the actions cell
  // is dynamic based on internal/external user status and publish status.
  const precedesActionsCell =
    numberIsTruthyOrZero(cellIndex) &&
    numberIsTruthyOrZero(cellsInRow) &&
    // Actions cell is the last cell, confirm this is the one immediately
    // preceding that
    cellIndex === cellsInRow - 2;

  const isActionsHeader =
    cell.column.id === MANAGE_LOCATIONS_COLUMN_FIELDS.actions && isHeaderCell;

  // If making changes that will alter the dimensions of cells ensure that the
  // virtualizer and inline styles in the component still work effectively
  return twMerge([
    ["pl-8", "py-[10px]", "text-start"],
    cell.column.id === MANAGE_LOCATIONS_COLUMN_FIELDS.locationName && [
      "pl-[18px]",
      "pr-8",
      "shadow-[inset_-1px_0px_0px_0px_#d9d9d9d9]",
      "min-w-[270px]",
    ],
    cell.column.id === MANAGE_LOCATIONS_COLUMN_FIELDS.wageType && [
      "min-w-[175px]",
    ],
    cell.column.id === MANAGE_LOCATIONS_COLUMN_FIELDS.status && [
      "min-w-[136px]",
    ],
    precedesActionsCell && ["pr-4"],
    cell.column.id === MANAGE_LOCATIONS_COLUMN_FIELDS.actions && [
      "min-w-[186px]",
      "pl-0",
      "shadow-[inset_1px_0px_0px_0px_#d9d9d9d9]",
    ],
    isActionsHeader && ["pl-4"],
    cell.column.getIsPinned() && ["bg-white", "sticky", "z-[1]"],
    // Additional padding to account for scroll bar
    isLastRow && ["pb-[16px]"],
    // Last row sits on the border of the table itself, avoid doubling borders
    !isHeaderCell && !isLastRow && ["border-uw-gray-2", "border-b"],
    isHeaderCell && ["shadow-[inset_0px_-1px_0px_0px_#d9d9d9d9]"],
  ]);
};

export const getManageLocationsPinnedColumnStyle = (
  column: Column<ScreenerLocationDraft>,
) => {
  const pinned = column.getIsPinned();

  switch (pinned) {
    case "left":
      return {
        left: `${column.getStart(pinned)}px`,
      };
    case "right":
      return {
        right: `${column.getAfter(pinned)}px`,
      };
    case false:
    default:
      return {};
  }
};
